import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"
import { Button, Input, Modal, Table, message, Spin } from "antd"
import {
  DeleteOutlined,
  DownOutlined,
  EyeOutlined,
  LinkOutlined,
} from "@ant-design/icons"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import "dayjs/locale/ar"
import { ReferralsApi } from "../../Apis/referralsApi"
import { translations } from "../../utils/Translation/staticTranslation"

dayjs.extend(relativeTime)

const PAGE_SIZE = 10
const MODAL_REWARDS_PAGE_SIZE = 5

const formatRewards = (value) => {
  const n = typeof value === "string" ? parseFloat(value) : Number(value)
  if (!Number.isFinite(n)) return "SAR 0.00"
  const text = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `SAR ${text}`
}

const mapApiRow = (row, lang) => {
  const id = row.id
  return {
    key: id,
    id,
    user: row.user_name?.trim() || "—",
    email: (row.user_email || "").trim(),
    code: (row.referral_code || "").trim() || "—",
    referralLink: (row.referral_link || "").trim(),
    signups: Number(row.signups) || 0,
    conversions: Number(row.conversions) || 0,
    rewards: formatRewards(row.rewards),
    lastActivity: row.last_activity_at
      ? dayjs(row.last_activity_at)
          .locale(lang === "ar" ? "ar" : "en")
          .fromNow()
      : "—",
  }
}

const mapModalRewardRow = (r) => ({
  key: r.id,
  user: r.user_name?.trim() || "—",
  email: (r.user_email || "").trim(),
  amount: formatRewards(r.amount),
  date: (r.earned_date || "—").trim(),
})

/** Ant Design injects close-button CSS after our bundle; force physical left for Arabic. */
const pinReferralModalCloseToLeft = (modalClassPrefix, topPx, leftPx) => {
  const raw = String(localStorage.getItem("language") || "ar").toLowerCase()
  if (raw !== "ar" && !raw.startsWith("ar-")) return
  const modalEl = document.querySelector(`.${modalClassPrefix}.ant-modal`)
  const btn = modalEl?.querySelector(".ant-modal-close")
  if (!btn) return
  btn.style.setProperty("top", `${topPx}px`, "important")
  btn.style.setProperty("left", `${leftPx}px`, "important")
  btn.style.setProperty("right", "auto", "important")
  btn.style.setProperty("inset-inline-start", `${leftPx}px`, "important")
  btn.style.setProperty("inset-inline-end", "auto", "important")
}

const copyToClipboard = async (text, labels) => {
  const t = String(text || "").trim()
  if (!t) return
  try {
    await navigator.clipboard.writeText(t)
    message.success(labels.copied)
  } catch {
    message.error(labels.copy_failed)
  }
}

const UsersTab = ({ readOnly = false, canDelete = true }) => {
  const lang = localStorage.getItem("language") || "ar"
  const isArabicLang = String(lang).toLowerCase().startsWith("ar")
  const t = translations[lang]?.referralsPage || translations.en.referralsPage

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [modalPage, setModalPage] = useState(1)
  const [userDetailLoading, setUserDetailLoading] = useState(false)
  const [userProfile, setUserProfile] = useState(null)
  const [userRewardsRows, setUserRewardsRows] = useState([])
  const [userRewardsCount, setUserRewardsCount] = useState(0)
  const [exportLoading, setExportLoading] = useState(false)
  const [deletingUserId, setDeletingUserId] = useState(null)

  const pinUserModalClose = useCallback(() => {
    pinReferralModalCloseToLeft("ref-user-details-modal", 12, 16)
  }, [])

  useLayoutEffect(() => {
    if (!isUserModalOpen) return
    pinUserModalClose()
    const raf = requestAnimationFrame(pinUserModalClose)
    const t0 = window.setTimeout(pinUserModalClose, 0)
    const t1 = window.setTimeout(pinUserModalClose, 120)
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(t0)
      window.clearTimeout(t1)
    }
  }, [isUserModalOpen, userDetailLoading, pinUserModalClose])

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch((prev) => {
        const next = search.trim()
        if (prev !== next) setPage(1)
        return next
      })
    }, 350)
    return () => clearTimeout(t)
  }, [search])

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const res = await ReferralsApi.getReferralUsers({
        page,
        page_size: PAGE_SIZE,
        search: debouncedSearch || undefined,
      })
      const payload = res.data
      const result = payload?.result
      if (payload?.success && result && Array.isArray(result.results)) {
        setRows(result.results.map((row) => mapApiRow(row, lang)))
        setTotal(
          typeof result.count === "number"
            ? result.count
            : result.results.length,
        )
      } else {
        setRows([])
        setTotal(0)
        message.error("Could not load referral users.")
      }
    } catch (e) {
      setRows([])
      setTotal(0)
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Failed to load referral users.",
      )
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch, lang])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const openUserDetailModal = useCallback(async (row) => {
    if (readOnly) return
    if (row?.id == null) return
    setSelectedUser(row)
    setModalPage(1)
    setUserProfile(null)
    setUserRewardsRows([])
    setUserRewardsCount(0)
    setIsUserModalOpen(true)
    setUserDetailLoading(true)
    try {
      const res = await ReferralsApi.getReferralUserDetail(row.id)
      const payload = res.data
      const result = payload?.result
      if (payload?.success && result) {
        setUserProfile(result.profile ?? null)
        const rm = result.rewards_management
        const list = rm?.results
        setUserRewardsCount(
          typeof rm?.count === "number" ? rm.count : Array.isArray(list) ? list.length : 0,
        )
        setUserRewardsRows(Array.isArray(list) ? list.map(mapModalRewardRow) : [])
      } else {
        setUserProfile(null)
        setUserRewardsRows([])
        setUserRewardsCount(0)
        message.error("Could not load user details.")
      }
    } catch (e) {
      setUserProfile(null)
      setUserRewardsRows([])
      setUserRewardsCount(0)
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Failed to load user details.",
      )
    } finally {
      setUserDetailLoading(false)
    }
  }, [readOnly])

  const modalDisplayUserId = userProfile?.user_id ?? selectedUser?.id ?? 0

  const modalRewardsPageData = useMemo(() => {
    const start = (modalPage - 1) * MODAL_REWARDS_PAGE_SIZE
    return userRewardsRows.slice(start, start + MODAL_REWARDS_PAGE_SIZE)
  }, [userRewardsRows, modalPage])

  const handleDeleteUser = useCallback(
    (row) => {
      if (readOnly || !canDelete) return
      if (row?.id == null) return

      const userLabel = row.user?.trim() || row.email?.trim() || String(row.id)
      Modal.confirm({
        title: t.confirm_delete_user_title,
        content: (t.confirm_delete_user_message || "").replace(
          "{name}",
          userLabel,
        ),
        okText: t.delete_user,
        okType: "danger",
        cancelText: t.delete_cancel,
        onOk: async () => {
          setDeletingUserId(row.id)
          try {
            const res = await ReferralsApi.deleteReferralUser(row.id)
            const payload = res?.data
            if (payload?.success === false) {
              throw new Error(
                payload?.message || payload?.error || "Failed to delete user.",
              )
            }
            message.success(t.delete_user_success)
            if (selectedUser?.id === row.id) {
              setIsUserModalOpen(false)
              setSelectedUser(null)
              setUserProfile(null)
              setUserRewardsRows([])
              setUserRewardsCount(0)
              setModalPage(1)
            }
            if (rows.length === 1 && page > 1) {
              setPage(page - 1)
            } else {
              await loadUsers()
            }
          } catch (e) {
            message.error(
              e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                t.delete_user_failed,
            )
          } finally {
            setDeletingUserId(null)
          }
        },
      })
    },
    [readOnly, canDelete, t, selectedUser?.id, rows.length, page, loadUsers],
  )

  const handleExportCsv = useCallback(async () => {
    if (readOnly) return
    setExportLoading(true)
    try {
      const res = await ReferralsApi.exportReferralUsersCsv()
      const blob = res?.data
      if (!(blob instanceof Blob)) {
        message.error("Could not download export.")
        return
      }
      const cd = res.headers?.["content-disposition"]
      const match = cd?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
      const fallback = `referral-users-${dayjs().format("YYYY-MM-DD")}.csv`
      const filename = match?.[1]?.trim() || fallback
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      message.success("Export downloaded.")
    } catch (e) {
      const errBody = e?.response?.data
      if (errBody instanceof Blob) {
        try {
          const text = await errBody.text()
          const j = JSON.parse(text)
          message.error(j.message || j.error || j.detail || "Failed to export users.")
        } catch {
          message.error("Failed to export users.")
        }
      } else {
        message.error(
          errBody?.message ||
            errBody?.error ||
            e?.message ||
            "Failed to export users.",
        )
      }
    } finally {
      setExportLoading(false)
    }
  }, [readOnly])

  const usersColumns = useMemo(
    () => [
      {
        title: t.col_user,
        dataIndex: "user",
        key: "user",
        render: (_, row) => (
          <div className="ref-users-user-cell">
            <div className={`ref-users-avatar avatar-${Number(row.id) % 6}`}>
              {row.user.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="ref-users-user-name">{row.user}</div>
              <div className="ref-users-user-email">{row.email || "—"}</div>
            </div>
          </div>
        ),
        width: 280,
      },
      {
        title: t.col_referral_code,
        dataIndex: "code",
        key: "code",
        render: (code) => (
          <span className="ref-users-code-cell">
            <span className="ref-code-pill ref-users-code-pill" title={code}>
              {code}
            </span>
            <button
              type="button"
              className="ref-users-copy-btn"
              aria-label="Copy referral code"
              disabled={readOnly}
              onClick={() => !readOnly && copyToClipboard(code, t)}
            >
              <img
                src="/dashboard/copy_icon.svg"
                alt=""
                className="ref-users-copy-icon"
              />
            </button>
          </span>
        ),
        width: 220,
      },
      { title: t.signup_label, dataIndex: "signups", key: "signups" },
      {
        title: t.conversions_label,
        dataIndex: "conversions",
        key: "conversions",
      },
      { title: t.rewards_label, dataIndex: "rewards", key: "rewards" },
      {
        title: t.last_activity,
        dataIndex: "lastActivity",
        key: "lastActivity",
      },
      {
        title: t.col_actions,
        key: "actions",
        align: "center",
        width: 130,
        render: (_, row) => (
          <span className="ref-users-actions-cell">
            <EyeOutlined
              className="ref-users-eye-icon"
              onClick={() => !readOnly && openUserDetailModal(row)}
              style={{
                cursor: readOnly ? "not-allowed" : "pointer",
                opacity: readOnly ? 0.45 : 1,
              }}
            />
            <LinkOutlined
              className="ref-users-link-icon"
              onClick={() => !readOnly && copyToClipboard(row.referralLink, t)}
              style={{
                cursor: readOnly ? "not-allowed" : "pointer",
                opacity: readOnly ? 0.45 : 1,
              }}
            />
            {canDelete && (
              <DeleteOutlined
                className="ref-users-delete-icon"
                onClick={() =>
                  !readOnly &&
                  deletingUserId !== row.id &&
                  handleDeleteUser(row)
                }
                style={{
                  cursor:
                    readOnly || deletingUserId === row.id
                      ? "not-allowed"
                      : "pointer",
                  opacity: readOnly || deletingUserId === row.id ? 0.45 : 1,
                }}
              />
            )}
          </span>
        ),
      },
    ],
    [
      openUserDetailModal,
      handleDeleteUser,
      deletingUserId,
      t,
      readOnly,
      canDelete,
    ],
  )

  const tableData = rows

  return (
    <div className="referrals-simple-view">
      <div className="referrals-subheader">
        <h2>{t.users_title}</h2>
        <button
          className="ref-button-primary ref-users-export-btn"
          type="button"
          disabled={readOnly || exportLoading}
          title={readOnly ? t.read_only_hint : undefined}
          onClick={handleExportCsv}
        >
          {exportLoading ? t.exporting : t.export}
          <DownOutlined />
        </button>
      </div>
      <div className="ref-panel ref-users-table-panel">
        <div className="ref-users-filters-row">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.search_users}
            className="ref-users-search-input"
            allowClear
          />
        </div>
        <Spin spinning={loading}>
          <Table
            className="ref-ant-table ref-users-table"
            columns={usersColumns}
            dataSource={tableData}
            pagination={{
              current: page,
              total,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              position: ["bottomRight"],
              itemRender: (pageNumber, type, originalElement) => {
                if (type === "prev")
                  return (
                    <span className="ref-users-pagination-link">{t.pagination_prev}</span>
                  )
                if (type === "next")
                  return (
                    <span className="ref-users-pagination-link">{t.pagination_next}</span>
                  )
                if (type === "jump-prev" || type === "jump-next")
                  return (
                    <span className="ref-users-pagination-ellipsis">...</span>
                  )
                if (type === "page") {
                  const isActive = pageNumber === page
                  return (
                    <span
                      className={`ref-users-pagination-page ${isActive ? "active" : ""}`}
                    >
                      {pageNumber}
                    </span>
                  )
                }
                return originalElement
              },
              onChange: (nextPage) => setPage(nextPage),
            }}
            size="middle"
          />
        </Spin>
      </div>
      <Modal
        open={isUserModalOpen}
        onCancel={() => {
          setIsUserModalOpen(false)
          setUserProfile(null)
          setUserRewardsRows([])
          setUserRewardsCount(0)
          setModalPage(1)
        }}
        footer={null}
        width={920}
        className={`ref-user-details-modal${isArabicLang ? " ref-user-details-modal--ar" : ""}`}
        closeIcon={<span className="ref-user-modal-close">×</span>}
        afterOpenChange={(open) => {
          if (open) {
            pinUserModalClose()
            window.setTimeout(pinUserModalClose, 0)
            window.setTimeout(pinUserModalClose, 200)
          }
        }}
      >
        <Spin spinning={userDetailLoading}>
          <div className="ref-user-modal-header">
            <div
              className={`ref-users-avatar ref-user-modal-avatar avatar-${Number(modalDisplayUserId) % 6}`}
            >
              {(
                userProfile?.user_name ||
                selectedUser?.user ||
                "U"
              )
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <div>
              <div className="ref-user-modal-name">
                {userProfile?.user_name || selectedUser?.user || "User"}
              </div>
              <div className="ref-user-modal-email">
                {userProfile?.user_email || selectedUser?.email || "—"}
              </div>
            </div>
          </div>

          <div className="ref-user-modal-stats">
            <div>
              <span>{t.modal_signups}</span>
              <strong>
                {userProfile?.signups ?? selectedUser?.signups ?? 0}
              </strong>
            </div>
            <div>
              <span>{t.modal_conversions}</span>
              <strong>
                {userProfile?.conversions ?? selectedUser?.conversions ?? 0}
              </strong>
            </div>
            <div>
              <span>{t.modal_reward_earned}</span>
              <strong>
                {userProfile
                  ? formatRewards(userProfile.reward_earned)
                  : selectedUser?.rewards ?? "SAR 0.00"}
              </strong>
            </div>
          </div>

          <div className="ref-user-modal-links">
            <div className="ref-user-modal-link-row">
              <label>{t.modal_referral_code}</label>
              <div className="ref-user-modal-link-input-wrap">
                <Input
                  value={(
                    userProfile?.referral_code ||
                    selectedUser?.code ||
                    ""
                  ).toUpperCase()}
                  readOnly
                />
                <Button
                  type="button"
                  className="ref-user-modal-copy-btn"
                  disabled={readOnly}
                  onClick={() =>
                    !readOnly &&
                    copyToClipboard(
                      userProfile?.referral_code || selectedUser?.code,
                    )
                  }
                >
                  <img src="/dashboard/copy_icon.svg" alt="" />
                  {t.copy}
                </Button>
              </div>
            </div>
            <div className="ref-user-modal-link-row">
              <label>{t.modal_referral_link}</label>
              <div className="ref-user-modal-link-input-wrap">
                <Input
                  value={
                    userProfile?.referral_link ||
                    selectedUser?.referralLink ||
                    ""
                  }
                  readOnly
                />
                <Button
                  type="button"
                  className="ref-user-modal-copy-btn"
                  disabled={readOnly}
                  onClick={() =>
                    !readOnly &&
                    copyToClipboard(
                      userProfile?.referral_link || selectedUser?.referralLink,
                    )
                  }
                >
                  <img src="/dashboard/copy_icon.svg" alt="" />
                  {t.copy}
                </Button>
              </div>
            </div>
          </div>

          <div className="ref-user-modal-rewards-title">{t.rewards_management}</div>
          <Table
            className="ref-ant-table ref-user-modal-rewards-table"
            columns={[
              {
                title: t.col_user,
                dataIndex: "user",
                key: "user",
                render: (_, row) => (
                  <div className="ref-users-user-cell">
                    <div
                      className={`ref-users-avatar avatar-${Number(row.key) % 6}`}
                    >
                      {row.user.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="ref-users-user-name">{row.user}</div>
                      <div className="ref-users-user-email">{row.email}</div>
                    </div>
                  </div>
                ),
              },
              { title: t.col_amount, dataIndex: "amount", key: "amount" },
              { title: t.col_earned_date, dataIndex: "date", key: "date" },
            ]}
            dataSource={modalRewardsPageData}
            pagination={{
              total: userRewardsCount,
              current: modalPage,
              pageSize: MODAL_REWARDS_PAGE_SIZE,
              showSizeChanger: false,
              position: ["bottomRight"],
              itemRender: (pageNumber, type, originalElement) => {
                if (type === "prev")
                  return (
                    <span className="ref-user-modal-pagination-link">
                      {t.pagination_prev}
                    </span>
                  )
                if (type === "next")
                  return (
                    <span className="ref-user-modal-pagination-link">
                      {t.pagination_next}
                    </span>
                  )
                if (type === "jump-prev" || type === "jump-next")
                  return (
                    <span className="ref-user-modal-pagination-ellipsis">
                      ...
                    </span>
                  )
                if (type === "page") {
                  const isActive = pageNumber === modalPage
                  return (
                    <span
                      className={`ref-user-modal-pagination-page ${isActive ? "active" : ""}`}
                    >
                      {pageNumber}
                    </span>
                  )
                }
                return originalElement
              },
              onChange: (nextPage) => setModalPage(nextPage),
            }}
            size="middle"
          />
        </Spin>
      </Modal>
    </div>
  )
}

export default UsersTab
